import config from '../../../shop.config.json';

/**
 * Phase 0 design-token reference — PRESERVED, not dead code.
 *
 * Mounted at /dev/swatches. This is the canonical visual check that the
 * Marble Crest v3 tokens (cream canvas, marble-as-accent-only, navy-as-ink)
 * still render correctly after a Tailwind or token change. Keep it working.
 *
 * The real marketing pages live in src/routes/site/ and pull copy from
 * src/content and facts from shop.config.json.
 *
 * Original note — mirrors the real hero pattern from the design bundle:
 *   • Cream canvas everywhere
 *   • Marble as accent ONLY where the logo sits (right panel on desktop)
 *   • Navy is INK, not fill
 *
 * Replaced Week 2 with the full hero + booking wizard + barber grid + gallery + visit.
 */
export default function DevSwatchesPage() {
  return (
    <div className="min-h-screen bg-page">
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-6 pt-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-tile bg-marble-sm bg-cover bg-center border border-edge-2" />
          <div>
            <p className="font-serif text-lg text-navy leading-tight">
              {config.shop.name}
            </p>
            <p className="kd-kicker text-gold-text text-[10px] tracking-wide">
              {config.shop.city.toUpperCase()}, {config.shop.state} · SINCE 2022
            </p>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-navy font-serif">
          <span>Book</span>
          <span>Barbers</span>
          <span>Gallery</span>
          <span>Walk-ins</span>
          <span>Visit</span>
        </nav>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline text-navy font-serif text-sm">
            {config.shop.phone_display}
          </span>
          <button className="bg-navy text-cream px-5 py-2.5 rounded-pill font-serif text-sm shadow-shell">
            Book a chair
          </button>
        </div>
      </header>

      {/* Hero — cream canvas, marble ACCENT on right */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12 items-center">
        {/* Left column: navy typography on cream */}
        <div>
          <div className="inline-flex items-center gap-2 bg-white border border-edge-2 rounded-pill px-4 py-2 mb-8 shadow-barber">
            <span className="text-lg">🇵🇷</span>
            <span className="font-serif text-sm text-navy">
              Est. 2022 · Zephyrhills, Florida
            </span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-navy mb-8">
            Come sit down.<br />
            We saved you a chair.
          </h1>
          <p className="font-serif text-lg text-slate-body mb-10 max-w-md">
            Five barbers, one crown. Book a time that suits you, or walk in —{' '}
            <span className="italic">sin cita, sin problema</span> — and we'll
            text you when the chair is warm.
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            <button className="bg-navy text-cream px-6 py-3 rounded-pill font-serif shadow-shell">
              Book a chair
            </button>
            <button className="border border-edge-3 text-navy px-6 py-3 rounded-pill font-serif bg-white">
              Join the walk-in line
            </button>
          </div>
          <div className="inline-flex items-center gap-3 bg-white border border-edge-2 rounded-pill px-4 py-2 shadow-barber">
            <span className="w-2 h-2 rounded-full bg-red-accent animate-pulse" />
            <span className="font-serif text-sm text-navy">
              About 25 minutes right now
            </span>
            <span className="font-serif italic text-xs text-warm-muted">
              · three chairs cutting, four folks waiting
            </span>
          </div>
        </div>

        {/* Right column: marble accent panel where the logo sits */}
        <div className="relative aspect-[3/4] bg-marble bg-cover bg-center rounded-panel border-2 border-gold/40 shadow-shell flex items-center justify-center">
          <div className="text-center">
            <div className="font-serif text-7xl font-semibold text-navy">KD</div>
            <div className="kd-kicker text-navy text-sm mt-1 tracking-wider">
              BARBERSHOP
            </div>
            <div className="font-serif italic text-xs text-slate-body mt-4">
              (real logo card drops in Week 2)
            </div>
          </div>
        </div>
      </section>

      {/* Scaffold verification — swatches proving tokens */}
      <section className="max-w-4xl mx-auto px-6 py-16 border-t border-edge-2">
        <p className="kd-kicker text-gold-text text-xs mb-3">Scaffold Verification</p>
        <h2 className="font-serif text-3xl text-navy mb-8">
          Brand tokens rendering correctly
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { name: 'Navy', hex: '#0B1B3A', className: 'bg-navy' },
            { name: 'Red Accent', hex: '#C8201C', className: 'bg-red-accent' },
            { name: 'Gold', hex: '#C9A227', className: 'bg-gold' },
            { name: 'Paper', hex: '#F8F4EC', className: 'bg-paper border border-edge-2' },
            { name: 'Marble', hex: 'accent', className: 'bg-marble bg-cover' },
          ].map((swatch) => (
            <div key={swatch.name} className="rounded-tile overflow-hidden shadow-card">
              <div className={`${swatch.className} h-20`} />
              <div className="bg-white p-3">
                <p className="kd-kicker text-xs text-navy">{swatch.name}</p>
                <p className="font-sans text-xs text-slate-body mt-1">{swatch.hex}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-edge-2 py-8 text-center">
        <p className="kd-kicker text-gold-text text-xs">
          {config.shop.address_line_1} · {config.shop.city}, {config.shop.state} {config.shop.zip}
        </p>
        <p className="font-serif text-sm text-slate-body mt-2">
          {config.shop.phone_display}
        </p>
      </footer>
    </div>
  );
}
